import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { cn } from "../../../common/u1/cn";
import Search from "../../Search";
import gemMark from "../../../../assets/images/logoXL.svg";
import { useGetUserQuery } from "../../../../store/auth/usersApiSlice";
import { setUserInfo } from "../../../../store/auth/authSlice";

/* The home banner. Rebuilt in Tailwind from HeroSection.jsx, which drove everything through .hero-* rules in styles/home-v2.css. NOTHING about the content... */

/* The overlay header's height. The hero reserves it as padding so the eyebrow
   never lands under the bar. One constant, used by both. */
export const PUBHEAD_H = 76;

/* The backdrop, matching the handoff's .hero-bg stack: a masked grid, the aurora glow behind the headline, the oversized TREVEX gem at the lower right, and a... */
const GRID_MASK =
  "radial-gradient(120% 80% at 50% 30%, #000 0%, rgba(0,0,0,.35) 55%, transparent 82%)";

const Backdrop = () => (
  <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
    <div
      className="absolute inset-[-2px]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(133,178,204,.10) 1px, transparent 1px)," +
          "linear-gradient(90deg, rgba(133,178,204,.10) 1px, transparent 1px)",
        backgroundSize: "62px 62px",
        WebkitMaskImage: GRID_MASK,
        maskImage: GRID_MASK,
      }}
    />

    {/* The warm aurora behind the headline. */}
    <div
      className="absolute left-1/2 top-[-220px] h-[860px] w-[1180px] -translate-x-1/2"
      style={{
        background:
          "radial-gradient(ellipse at center, rgba(245,157,21,.20) 0%, rgba(234,85,57,.10) 40%, transparent 70%)",
      }}
    />

    {/* The gem, oversized and nearly transparent at the lower right. It is the
        brand mark itself rather than a hand-drawn stand-in. */}
    <img
      src={gemMark}
      alt=""
      width="520"
      height="552"
      className="absolute bottom-[-120px] right-[-40px] w-[380px] opacity-[0.06] lg:w-[520px]"
    />

    {/* Seats the banner against the section below it. */}
    <div
      className="absolute inset-0"
      style={{
        background:
          "radial-gradient(120% 90% at 50% 20%, transparent 40%, rgba(0,26,45,.55) 100%)",
      }}
    />
  </div>
);

export const HomeHero = () => {
  const dispatch = useDispatch();
  const { token, userInfo } = useSelector((state) => state.auth);
  /* Transcribed from HeroSection: the home page is where the session user is
     refreshed, and the guard is the same. */
  const { data, isSuccess } = useGetUserQuery(token, { skip: !token && !userInfo });

  useEffect(() => {
    if (isSuccess) dispatch(setUserInfo({ ...data }));
  }, [data, dispatch, isSuccess]);

  return (
    <section
      data-component="Banner"
      data-testid="home-hero"
      className={cn(
        /* NO overflow-hidden here. The country menu is absolutely positioned inside the search bar and an overflow-hidden ancestor CLIPS it — it was being cut off at... */
        "relative z-[1] pb-24 text-white",
        "bg-[linear-gradient(160deg,var(--navy-deep)_0%,var(--navy)_55%,var(--navy-3)_100%)]"
      )}
      style={{ paddingTop: PUBHEAD_H + 72 }}
    >
      <Backdrop />

      <div className="container-wide relative">
        <div className="mb-7 flex justify-center">
          <span
            className={cn(
              "inline-flex items-center gap-2.5 rounded-full px-4 py-2",
              "border border-[rgba(245,157,21,.35)] bg-[rgba(245,157,21,.08)]",
              "text-[11px] font-semibold uppercase tracking-[.22em] text-[#F6C857]"
            )}
            style={{ fontFamily: '"DM Mono", monospace' }}
          >
            <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-[var(--gold)]" />
            The UAE Business Directory
          </span>
        </div>

        {/* `!` on eight utilities: `.tvx-home h1` is (0,1,1) and sets colour, size, weight, line-height, tracking AND `margin: 0`, which outranks a plain utility class. */}
        <h1
          className={cn(
            "!mx-auto !mb-[22px] !mt-6 max-w-[16ch] text-center",
            "!text-white !font-extralight",
            "!text-[clamp(40px,8.2vw,120px)] !leading-[0.96] !tracking-[-0.045em]"
          )}
        >
          Your business is already here
          <span className="mr-[0.06em] font-normal tracking-[0.04em] text-[var(--coral)]">.</span>{" "}
          Make it yours
          <span className="mr-[0.06em] font-normal tracking-[0.04em] text-[var(--coral)]">.</span>
        </h1>

        <p
          className={cn(
            "mx-auto mt-3 mb-11 max-w-[820px] text-center text-[17px] leading-[1.55]",
            "tracking-[-0.005em] text-[#A6BCC9] sm:text-[19px]"
          )}
        >
          Every UAE business starts with a{" "}
          <b className="font-semibold text-white">free Listing</b>. Claim yours in
          three minutes.
        </p>

        {/* The existing search, unchanged — its own styling is the .hero-*
            rules this section still sits inside. */}
        <Search variant="hero" resultsPath="/search" />
      </div>
    </section>
  );
};

export default HomeHero;
